import { useCallback, useEffect, useState } from 'react';

import axios, { type AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import {
    DELETE_LTI_CREDENTIAL,
    REGENERATE_SHARED_SECRET
} from '../../config/apiPaths';
import { type LTICredential } from '../../types/apiTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls } from '../../utils/url';

export enum SsoView {
    List = 0,
    Edit = 1,
    Create = 2
}

export type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';


export type IdpInfoClient = {
    jwks_uri: string;
    authorization_endpoint: string;
};

export type IdpSettingsClient = {
    auto_provision_users: boolean;
    auto_update_users: boolean;
    claim_email?: string;
    claim_name?: string;
    claim_given_name?: string;
    claim_family_name?: string;
    claim_ext_id?: string;
    claim_roles?: string;
    role_ids_admin: string[];
    role_ids_instructor: string[];
    role_ids_inst_designer: string[];
    oidc_scope: string;
};

export type OidcIdpRecordClient = {
    id: string;
    display_name: string;
    display_position: number;
    is_active: boolean;
    client_id: string;
    domain: string;
    idp_info: IdpInfoClient;
    settings: IdpSettingsClient;
    created_at?: Date;
    updated_at?: Date;
};

export type OidcIdpUpdateDataClient = Omit<OidcIdpRecordClient, 'created_at' | 'updated_at' | 'idp_info' | 'display_position'>;

export type OidcIdpCreateDataClient = Omit<OidcIdpUpdateDataClient, 'id'>;

const OIDC_IDP_ENDPOINTS = '/v1/auth/oidc_idp';
type SsoErrorResponse = {
    error: string;
};
const useAdminSso = () => {
    const { idp } = useParams<{ idp: string }>();

    const { uuid, isLoading } = useContainer(AdminAuthContainer);
    const { api } = getUrls();
    const { t } = useTranslation('admin_settings');
    const [idps, setIdps] = useState<OidcIdpRecordClient[]>([]);
    const [ssoFetchStatus, setSsoFetchStatus] = useState<LoadingStatus>('INITIAL');
    const [ssoFetchError, setSsoFetchError] = useState<AxiosError<SsoErrorResponse> | undefined>(undefined);
    const [selectedIdp, setSelectedIdp] = useState<any>(null);
    const [idpId, setIdpId] = useState<string | null>(null);
    const [view, setView] = useState<SsoView>(SsoView.List);

    const { enqueueSnackbar } = useSnackbar();
    const fetchIntegrationList = useCallback(async () => {
        try {
            setSsoFetchStatus('LOADING');
            const { data } = await axios.get<OidcIdpRecordClient[]>(`${api}${OIDC_IDP_ENDPOINTS}`, {
                headers: getUserUuidHeaders(uuid)
            });
            setIdps(data);
            setSsoFetchStatus('SUCCESS');
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.fetch-all-credentials'));
            setSsoFetchStatus('ERROR');
        }
    }, [api, uuid, t, enqueueSnackbar]);

    const updateIdp = async (recordData: Partial<OidcIdpUpdateDataClient>) => {
        try {
            const { data } = await axios.post<LTICredential>(`${api}${OIDC_IDP_ENDPOINTS}`, recordData, {
                headers: getUserUuidHeaders(uuid)
            });
            const successMessage = t('sso.update-success', { title: recordData.display_name! });
            enqueueSnackbar(successMessage, { variant: 'success' });
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('sso.update-error'));
        }
    };
    const createIdp = async (recordData: Partial<OidcIdpCreateDataClient>) => {
        try {
            const { data } = await axios.post<LTICredential>(`${api}${OIDC_IDP_ENDPOINTS}`, recordData, {
                headers: getUserUuidHeaders(uuid)
            });
            const successMessage = t('sso.create-success', { title: recordData.display_name! });
            enqueueSnackbar(successMessage, { variant: 'success' });
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('sso.create-error'));
        }
    };

    const deleteIdp = async (recordData: Partial<OidcIdpRecordClient>) => {
        try {
            const { data } = await axios.delete(`${api}${OIDC_IDP_ENDPOINTS}?idp_id=${recordData.id!}`, {
                headers: getUserUuidHeaders(uuid)
            });
            const successMessage = t('sso.delete-success', { title: recordData.display_name! });
            enqueueSnackbar(successMessage, { variant: 'success' });
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('sso.delete-error'));
        }
    };

    const regenerateSecret = async () => {
        try {
            const { data } = await axios.post(`${api}${REGENERATE_SHARED_SECRET}`, {
                lti_creds_uuid: selectedIdp?.lti_creds_uuid,
                user_uuid: uuid
            });

            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.regenerate-secret'));
        }
    };

    const deleteIntegration = async () => {
        try {
            const { data } = await axios.delete(
                `${api}${DELETE_LTI_CREDENTIAL}?lti_creds_uuid=${selectedIdp?.lti_creds_uuid}`,
                { headers: getUserUuidHeaders(uuid) }
            );

            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.delete-integration'));
        }
    };

    useEffect(() => {
        if (!isLoading && uuid) {
            fetchIntegrationList();
        }
    }, [view, fetchIntegrationList, isLoading, uuid]);

    return {
        isLoading,
        fetchIntegrationList,
        idps,
        ssoFetchStatus,
        ssoFetchError,
        selectedIdp,
        setSelectedIdp,
        updateIdp,
        createIdp,
        deleteIdp,
        regenerateSecret,
        view,
        setView,
        uuid,
        idpId,
        setIdpId
    };
};

const AdminSsoContainer = createContainer(useAdminSso);

export default AdminSsoContainer;
